import React, { useCallback, useEffect, useState } from "react";

import { useScrollIntoView, useEventListener } from "@mantine/hooks";
import { Drawer, DrawerProps } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  Container,
  List,
  ListItem,
  ListTitle,
  Main,
  MainTitle,
  MainTitleLabel,
  MainTitleCloseButton,
  SideMenu,
  MainContent,
  Section,
  SectionTitle,
  SectionContent,
} from "./styled";

type DomNode = string | React.ReactNode | number | null;

interface SectionItem {
  id: string | number;
  title?: DomNode;
  content?: React.ReactNode;
  contentDisableGutters?: boolean;
}

interface AppViewerDrawerProps {
  title?: DomNode;
  listTitle?: DomNode;
  fullHeight?: boolean;
  DrawerProps?: DrawerProps;
  children?: React.ReactNode;
  idPrefix?: string;
  hideSideMenu?: boolean;

  sectionItems?: SectionItem[];
}

export const AppViewerDrawer = (props: AppViewerDrawerProps) => {
  const {
    title,
    listTitle,
    fullHeight,
    DrawerProps,
    sectionItems,
    children,
    hideSideMenu,
    idPrefix = "app-viewer-drawer-",
  } = props;
  const { onClose, open } = DrawerProps ?? {};
  const { t: translate } = useTranslation();
  const [currentActive, setCurrentActive] = useState<string | number>();
  const idGenerator = (id: string | number) => {
    return `${idPrefix}${id}`;
  };

  const { scrollIntoView, targetRef, scrollableRef } = useScrollIntoView<HTMLElement, HTMLElement>({
    offset: 60,
  });
  const handleScrollIntoView = (id: string | number) => {
    const element = document.getElementById(idGenerator(id));

    if (!element) return;
    (targetRef as React.MutableRefObject<HTMLElement | null>).current = element;
    scrollIntoView();
    setCurrentActive(id);
  };
  const handleWheel = useCallback(
    (event) => {
      if (!sectionItems) return;
      let nextIndex;

      if (event.wheelDelta <= 0) {
        nextIndex = sectionItems.findIndex((nav) => nav.id === currentActive) + 1;
      } else {
        nextIndex = sectionItems.findIndex((nav) => nav.id === currentActive) - 1;
      }

      if (nextIndex !== undefined && nextIndex <= sectionItems.length - 1 && nextIndex >= 0) {
        const nextPage = sectionItems[nextIndex].id;
        const elementId = idGenerator(nextPage);
        const element = document.getElementById(elementId);

        const nextPageTop = element?.getBoundingClientRect()?.top;

        const isToShow =
          nextPageTop && event.wheelDelta <= 0
            ? nextPageTop <= 200
            : nextPageTop && nextPageTop >= 55;

        if (nextPageTop && isToShow) setCurrentActive(nextPage);
      }
    },
    [currentActive]
  );
  const mouseListenerRef = useEventListener("wheel", handleWheel);

  useEffect(() => {
    if (open && sectionItems?.length) setCurrentActive(sectionItems.at(0)!.id);
  }, [open]);

  const renderSideMenuList = () => {
    if (!sectionItems || hideSideMenu) return;

    return (
      <SideMenu>
        <List>
          <ListTitle>{listTitle ?? translate("navigation")}</ListTitle>
          {sectionItems.map((item: SectionItem) => {
            return (
              <ListItem
                key={item.id}
                isActive={currentActive === item.id}
                onClick={() => handleScrollIntoView(item.id)}
              >
                {item.title}
              </ListItem>
            );
          })}
        </List>
      </SideMenu>
    );
  };

  const renderSections = () => {
    if (!sectionItems) return;

    return sectionItems.map((item: SectionItem) => {
      return (
        <Section key={item.id} id={idGenerator(item.id)}>
          <SectionTitle>{item.title}</SectionTitle>

          <SectionContent disableGutters={item.contentDisableGutters}>
            {item.content}
          </SectionContent>
        </Section>
      );
    });
  };

  return (
    <Drawer
      anchor="right"
      ModalProps={{
        keepMounted: false,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disablePortal: true,
      }}
      PaperProps={{
        style: {
          overflow: "hidden",
          backgroundColor: "transparent",
          marginRight: "16px",
          marginTop: "16px",
          height: "calc(100% - 32px)",
          color: "inherit",
        },
      }}
      {...DrawerProps}
    >
      <Container fullHeight={fullHeight}>
        {renderSideMenuList()}

        <Main>
          <MainTitle>
            {title && <MainTitleLabel>{title}</MainTitleLabel>}

            <MainTitleCloseButton onClick={(event) => onClose?.(event, "backdropClick")} />
          </MainTitle>

          <MainContent
            ref={(node) => {
              if (!node) return;
              (scrollableRef as React.MutableRefObject<HTMLElement | null>).current = node;
              (mouseListenerRef as React.MutableRefObject<HTMLElement | null>).current = node;
            }}
          >
            {children}

            {renderSections()}
          </MainContent>
        </Main>
      </Container>
    </Drawer>
  );
};
